import { useId, useState } from "react";
import { ActionBar } from "../components/ActionBar";
import { Icon, type IconName } from "../components/Icon";
import { useFocusHeading } from "../lib/hooks";
import type { FlowCtx } from "../state/flow";
import { S } from "../strings";

export function ConsentScreen({ ctx }: { ctx: FlowCtx }) {
  const { session, w } = ctx;
  const c = session.consent;
  const already = !!session.progress.consentedAt;
  const [agree, setAgree] = useState(already);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const heading = useFocusHeading();
  const discId = useId();

  async function next() {
    if (!agree || busy) return;
    if (already) {
      ctx.go("offer");
      return;
    }
    setBusy(true);
    const s = await ctx.run(() => ctx.api.consent(c.version));
    setBusy(false);
    if (s) ctx.apply(s);
  }

  return (
    <>
      <main className="page" id="main">
        <div className="panel panel-pad narrow-panel">
          <div className="stack">
            <div>
              <div className="eyebrow">{S.consent.eyebrow}</div>
              <h1 className="title" ref={heading} tabIndex={-1}>
                {c.title}
              </h1>
              <p className="lede">{S.consent.lede(w)}</p>
            </div>
            <ul className="facts">
              {c.summary.map((f) => (
                <li className="fact" key={f.title}>
                  <Icon n={f.icon as IconName} />
                  <div>
                    <b>{f.title}</b>
                    <span>{f.text}</span>
                  </div>
                </li>
              ))}
            </ul>
            <div>
              <button type="button" className="btn-link disclose" aria-expanded={open} aria-controls={discId} onClick={() => setOpen(!open)}>
                {open ? S.consent.hideFull : S.consent.readFull} <Icon n={open ? "down" : "chev"} size={14} />
              </button>
              <div className="disclosure" id={discId} hidden={!open} tabIndex={0} role="region" aria-label={c.sections[0]?.heading ?? c.title}>
                {c.sections.map((sec) => (
                  <div key={sec.heading}>
                    <h2 className="disc-h">{sec.heading}</h2>
                    <p>{sec.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <label className={`check ${agree ? "on" : ""}`} htmlFor="consent-box">
              <input type="checkbox" id="consent-box" checked={agree} disabled={already} onChange={(e) => setAgree(e.target.checked)} aria-describedby="consent-help" />
              <span>
                <b>{c.agreeLabel}</b>
                <span className="help block" id="consent-help">
                  {c.agreeHelp}
                </span>
              </span>
            </label>
            <div className="row desk-actions">
              <button type="button" className="btn btn-primary" disabled={!agree || busy} onClick={() => void next()} aria-busy={busy}>
                {S.common.continue} <Icon n="chev" />
              </button>
              <button type="button" className="btn btn-quiet" onClick={() => ctx.openModal("paper")}>
                {S.consent.paper}
              </button>
            </div>
          </div>
        </div>
      </main>
      <ActionBar>
        <button type="button" className="btn btn-quiet" onClick={() => ctx.openModal("paper")}>
          {S.consent.paperShort}
        </button>
        <button type="button" className="btn btn-primary" disabled={!agree || busy} onClick={() => void next()}>
          {S.common.continue}
        </button>
      </ActionBar>
    </>
  );
}
